import type { ReactNode } from 'react'

import { MessagesNotesTagsControls } from '@/components/list/MessagesNotesTagsControls'
import { ConfirmationPanel } from '@/features/confirm/ConfirmationPanel'
import { ShipmentLabels } from '@/features/labels/ShipmentLabels'
import { useCancelLoad } from '@/hooks/shipments'
import { useCarrierPublicSettings } from '@/hooks/useCarrierPublicSettings'

import { CancelShipmentPrompt } from './CancelShipmentPrompt'
import { DetailsDocumentPanel } from './DetailsDocumentPanel'
import { DetailsQuotePanel } from './DetailsQuotePanel'
import { DetailsSharePanel } from './DetailsSharePanel'
import { RouteGuidePanel } from './RouteGuidePanel'
import { SliderContentBody, SliderContentHeader } from './SliderContentLayout'
import { TrackingPanel } from './TrackingPanel'
import type { ShipmentContentProps, SliderName } from './types'
import { UnCancelShipmentPrompt } from './UnCancelShipmentPrompt'

export const CancelPanel = ({
  setActiveSlider,
  load,
}: ShipmentContentProps) => {
  const cancelLoad = useCancelLoad(load.loadId)
  const { carrierPublicSettings } = useCarrierPublicSettings(
    load.selectedQuote?.carrierId,
  )
  const closePanel = () => setActiveSlider(undefined)

  return (
    <SliderContentBody>
      <SliderContentHeader {...load} prefix="Cancel Pickup for " />
      <CancelShipmentPrompt
        loading={cancelLoad.isPending}
        isAutoCancel={
          carrierPublicSettings?.cancelMethod === 'auto' ||
          carrierPublicSettings?.cancelMethod === 'email'
        }
        onDecline={closePanel}
        onConfirm={() => {
          cancelLoad.mutate(false, {
            onSuccess: closePanel,
          })
        }}
      />
    </SliderContentBody>
  )
}

export const MessagesPanel = ({ load }: ShipmentContentProps) => {
  return (
    <SliderContentBody>
      <SliderContentHeader {...load} />
      <MessagesNotesTagsControls
        className="flex flex-1 flex-col items-start"
        contentClass="flex-1 w-full"
        load={load}
        navClass="mb-6"
      />
    </SliderContentBody>
  )
}

export const UnCancelPanel = ({
  load,
  setActiveSlider,
}: ShipmentContentProps) => {
  return (
    <SliderContentBody>
      <SliderContentHeader {...load} prefix="Uncancel shipment for " />
      <UnCancelShipmentPrompt
        onClose={() => setActiveSlider(undefined)}
        loadId={load.loadId}
      />
    </SliderContentBody>
  )
}
export type SliderPanelComponent = (
  p: ShipmentContentProps & { closePanel: () => void },
) => ReactNode
export const sliderTabs: Record<SliderName, SliderPanelComponent> = {
  cancel: CancelPanel,
  uncancel: UnCancelPanel,
  confirm: ConfirmationPanel,
  documents: DetailsDocumentPanel,
  labels: ShipmentLabels,
  messages: MessagesPanel,
  quotes: DetailsQuotePanel,
  routeGuide: RouteGuidePanel,
  share: DetailsSharePanel,
  track: TrackingPanel,
}
